'use client';

import { c as _c } from "react/compiler-runtime";
import { css } from '@repo/pandacss/css';
import { Button } from '@repo/ui/Button';
import { LoadingSpinner } from '@repo/ui/LoadingSpinner';
import { Typography } from '@repo/ui/Typography';
import { useRef } from 'react';
import { useAnswer } from '@/entities/answer/model/hooks';
import { useFunnelStepId, useIsBranchLastStep, useIsFunnelSubmitting } from '@/features/funnel/model/Context/hooks';
import { useFunnelNavigation } from '@/features/funnel/model/useFunnelNavigation';
import { flattenOptions } from '@/features/funnel/ui/Form/SearchableGroupedMultiSelectInput/filterGroups';
import { SelectedChips } from '@/features/funnel/ui/Form/SearchableGroupedMultiSelectInput/SelectedChips';
import { getFunnelStepFormId, pendingFieldsetClass } from '@/features/funnel/ui/Form/utils';
import type { HydratedStepConfig } from '@/shared/config/funnel/types';
import { useVisualViewportKeyboardOffset } from './useVisualViewportKeyboardOffset';
type Props = {
  step: HydratedStepConfig;
  isStepSkippable: boolean;
  isAutoAdvance?: boolean;
};
export function Footer(t0) {
  const $ = _c(38);
  const {
    step,
    isStepSkippable,
    isAutoAdvance: t1
  } = t0;
  const isAutoAdvance = t1 === undefined ? false : t1;
  const footerRef = useRef(null);
  const keyboardOffset = useVisualViewportKeyboardOffset(footerRef);
  const stepId = useFunnelStepId();
  const {
    value,
    setAnswer
  } = useAnswer(stepId);
  const {
    advance
  } = useFunnelNavigation();
  const isLast = useIsBranchLastStep();
  const isSubmitting = useIsFunnelSubmitting();
  const hasAnswer = Array.isArray(value) ? value.length > 0 : value !== undefined;
  let t2;
  if ($[0] !== advance || $[1] !== isStepSkippable || $[2] !== setAnswer || $[3] !== step || $[4] !== stepId) {
    t2 = isStepSkippable ? () => {
      if (["searchable-grouped-multi-select", "multi-select"].includes(step.type)) {
        setAnswer([]);
        advance(stepId, []);
      } else {
        advance(stepId, undefined);
      }
    } : undefined;
    $[0] = advance;
    $[1] = isStepSkippable;
    $[2] = setAnswer;
    $[3] = step;
    $[4] = stepId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const onSkip = t2;
  if (isAutoAdvance && !isStepSkippable) {
    return null;
  }
  const isSearchableMultiSelect = step.type === "searchable-grouped-multi-select";
  let t3;
  if ($[6] !== isSearchableMultiSelect || $[7] !== step.asyncOptions || $[8] !== value) {
    t3 = isSearchableMultiSelect && Array.isArray(value) && value.length > 0 ? flattenOptions(step.asyncOptions).filter(option => value.includes(option.value)) : [];
    $[6] = isSearchableMultiSelect;
    $[7] = step.asyncOptions;
    $[8] = value;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const selectedChips = t3;
  const chipVariant = isSearchableMultiSelect ? step.chipVariant : "circle";
  let t4;
  if ($[10] !== setAnswer || $[11] !== value) {
    t4 = chipValue => {
      if (!Array.isArray(value)) {
        return;
      }
      setAnswer(value.filter(item => item !== chipValue));
    };
    $[10] = setAnswer;
    $[11] = value;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const removeChip = t4;
  let t5;
  if ($[13] !== keyboardOffset) {
    t5 = keyboardOffset > 0 ? {
      transform: `translateY(${keyboardOffset}px)`
    } : undefined;
    $[13] = keyboardOffset;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = css({
      position: "sticky",
      bottom: "0",
      left: "0",
      bg: "common.white",
      isolation: "isolate",
      zIndex: "[40]",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      overflow: {
        base: "scroll",
        md: "auto"
      },
      _before: {
        content: "\"\"",
        position: "absolute",
        top: "0",
        left: "[calc(50% - 50cqw - max(0px, (100vw - var(--wrapper-max)) / 2))]",
        width: "[calc(100cqw + max(0px, (100vw - var(--wrapper-max)) / 2))]",
        height: "[100%]",
        bg: "common.white",
        zIndex: "-1",
        boxShadow: "[0 -10px 10px 0 rgba(100, 102, 106, 0.05)]",
        borderTop: "[1px solid {colors.neutral.lighter-2}]"
      }
    });
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== chipVariant || $[17] !== isSubmitting || $[18] !== removeChip || $[19] !== selectedChips) {
    t7 = selectedChips.length > 0 && <fieldset disabled={isSubmitting} className={pendingFieldsetClass}><SelectedChips selected={selectedChips} onRemove={removeChip} variant={chipVariant} /></fieldset>;
    $[16] = chipVariant;
    $[17] = isSubmitting;
    $[18] = removeChip;
    $[19] = selectedChips;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = css({
      width: "[100%]",
      maxWidth: "[calc(640px - 1rem)]",
      mx: "auto",
      p: "3",
      display: "flex",
      flexDirection: "column",
      gap: "2"
    });
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== hasAnswer || $[23] !== isAutoAdvance || $[24] !== isLast || $[25] !== isStepSkippable || $[26] !== isSubmitting || $[27] !== onSkip || $[28] !== stepId) {
    t9 = isStepSkippable && !hasAnswer && onSkip ? <button type="button" className={css({
      bg: "[transparent]",
      border: "none",
      cursor: "pointer",
      py: "2",
      alignSelf: "center",
      _hover: {
        textDecoration: "underline"
      }
    })} onClick={onSkip} disabled={isSubmitting}><Typography as="span" variant="body" css={{
        color: "greens.dark",
        fontWeight: "semibold"
      }}>Skip this step</Typography></button> : !isAutoAdvance && <Button type="submit" form={getFunnelStepFormId(stepId)} size="medium" disabled={isSubmitting} aria-busy={isSubmitting} aria-label={isSubmitting ? "Loading" : undefined} className={css({
      textTransform: "uppercase",
      height: "var(--next-button-height)",
      fontSize: "[14px]",
      "&[aria-busy=true]:disabled": {
        bg: "greens.main",
        color: "common.white",
        cursor: "progress"
      },
      _focusVisible: {
        outlineOffset: "[4px]"
      }
    })}>{isSubmitting ? <LoadingSpinner variant="onPrimaryButton" /> : isLast ? "See results" : "Next"}</Button>;
    $[22] = hasAnswer;
    $[23] = isAutoAdvance;
    $[24] = isLast;
    $[25] = isStepSkippable;
    $[26] = isSubmitting;
    $[27] = onSkip;
    $[28] = stepId;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== t8 || $[31] !== t9) {
    t10 = <div className={t8}>{t9}</div>;
    $[30] = t8;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== t10 || $[34] !== t5 || $[35] !== t6 || $[36] !== t7) {
    t11 = <div ref={footerRef} style={t5} className={t6}>{t7}{t10}</div>;
    $[33] = t10;
    $[34] = t5;
    $[35] = t6;
    $[36] = t7;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  return t11;
}